/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable lines-between-class-members */
import { Logger } from 'lib/logging';
import { Axios as HTTPClient } from 'axios';

export interface SDKConfig {
  httpClient: HTTPClient;
  webSocketURL: string;
  pingIntervalInMs?: number;
  maxReconnectAttempts?: number;
  logger?: Logger;
}

export interface Subscription {
  callback: Callback;
  topics: Topic[];
}

export type Unsubscribe = () => void;

export type Callback = (event: HermesEvent) => void;

export interface HermesEvent {
  topic: Topic;
  payload?: unknown;
  messageID?: string; // TODO: needed?
  timestamp?: number; // TODO: needed?
  // TODO: Do we get version from Hermes?
}

export enum Topic {
  // Psuedo topics created just for the HermesWebSDK. These topics should not
  // be sent to or be received from the Hermes backend.
  HermesWebSDKConnecting = 'HermesWebSDKConnecting',
  HermesWebSDKConnected = 'HermesWebSDKConnected',
  HermesWebSDKDisconnecting = 'HermesWebSDKDisconnecting',
  HermesWebSDKDisconnected = 'HermesWebSDKDisconnected',
  HermesWebSDKConnectionError = 'HermesWebSDKConnectionError',
  HermesWebSDKReconnectionFailure = 'HermesWebSDKReconnectionFailure',

  // ---
  // Actual topics which are sent to or received from the Hermes backend

  // ---
  // Hermes WebSocket Topics
  HermesHeartbeat = '1',   // TODO: How is this used?
  HermesRelayFailed = '2', // TODO: How is this used?
  HermesFarewell = '5',    // TODO: How is this used?
  HermesPing = '6',
  HermesPong = '7',

  // ---
  // Message Center Topics
  // https://wiki.grab.com/display/RTC/TCP+Message+Spec
  MCNewOrUpdatedChatroom = '1301',
  MCSendMessage = '1302',
  MCNewMessage = '1303',
  MCPresenceNotify = '1304',
  MCSystemACK = '1400',
  MCClientACK = '1401',
  MCMessageReceiptACK = '1402',

  // ---
  // Workforce Topics
  // https://wiki.grab.com/display/CSE/TCP+Message+Spec
  WorkforceAcceptChat = '1600',
  WorkforceTicketPayload = '1601',
  WorkforceEndChat = '1602',

  // ---
  // Order Manager Topics
  // https://wiki.grab.com/display/GRABFOOD/Mex+Hermes+Message#Mex%20New%20Order%20(1530)
  NewOrderNotification = '1530'
}

// Non-exhaustive
export enum ErrorCode {
  // HTTP error status codes
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  TooManyRequests = 429,
  InternalServerError = 500,
  BadGateway = 502,
  ServiceUnavailable = 503,
  GatewayTimeout = 504,
}

export enum ErrorTarget {
  Authenticate = 'Authenticate',
}

// Avoid using `HermesError` for errors which are published to subscribers.
// Use plain data objects as they can be passed around and JSON-stringified
// easily as compared to `Error` types.
export class HermesError extends Error {
  code?: number;

  target: ErrorTarget;

  constructor(message: string, target: ErrorTarget, code?: number) {
    super(message);
    Object.setPrototypeOf(this, HermesError.prototype);
    this.target = target;
    this.code = code;
  }
}

export const LogOperationNames = {
  WSAuthenticate: "WEBSOCKET_AUTH",
  WSConnect: "WEBSOCKET_CONNECT",
  WSClose: "WEBSOCKET_CLOSE",
  WSSend: "WEBSOCKET_SEND",
  WSOpen: "WEBSOCKET_OPEN",
  WSReceive: "WEBSOCKET_RECEIVE",
  WSError: "WEBSOCKET_ERROR",
  NotifySubscriber: "NOTIFY_SUBSCRIBER",
}

export const OPERATION_KEY = "operation";
